import { BarcodeAPI, ModalButton, ModalProps, ModalService } from '@philips-internal/oswald-ui-components';
import { DeckAPI, FetchAPI } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, ModalType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import { AbortableCloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { HistoryService } from '@philips-internal/oswald-ui-core/lib/services';

import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { barcodeResponseType, ConfirmExamCollectionResponse, ListPendingParams, SelectedBarcodeExam } from '../../../../interfaces/exam-collect/pending-exam-collection';
import { ExamCollectionUtils } from '../../../../utils/exam-collection-utils';
import { PendingExamsModalId } from '../../selected-pending-exams/selected-pending-exams-controller';
import ModalHandler from './pending-exam-collection-modal-handler';
import ToastHandler from './pending-exam-collection-toast-handler';

const historyService = HistoryService;

enum ReadPendingExamBarcodeResponseType {
  SUBMIT_EXECUTED,
  CHANGE_EXAM_STATUS_SUCCESS,
  CHANGE_EXAM_STATUS_FAIL,
  ADD_EXAMS_TO_COLLECTION_LIST_SUCCESS,
  INVALID_BARCODE_ERROR,
  EXAM_NOT_IN_PRESCRIPTION_ERROR,
  INVALID_COLLECTION_TIME_ERROR
}

interface ExamCollectionWithBarcodeHandlerParameters {
  paramUseLaboratorySampleStandard?: string;
}

class ExamCollectionWithBarcodeHandler {
  static initialize(parameters: ExamCollectionWithBarcodeHandlerParameters): void {
    const paramUseLaboratory = parameters.paramUseLaboratorySampleStandard;
    this.parameters = {
      paramUseLaboratorySampleStandard: paramUseLaboratory ? paramUseLaboratory : 'S'
    };
    this.beepedBarcodes = [];
    this.closingBarcodeReader = false;
  }

  private static parameters: ExamCollectionWithBarcodeHandlerParameters;
  private static beepedBarcodes: SelectedBarcodeExam[] = [];
  private static closingBarcodeReader: boolean;
  private static programingClose: boolean;

  static async onBarcodeReadSuccess(barcode: string, barcodeApi: BarcodeAPI, deckPendingExams: DeckAPI, fecthPendingExam: FetchAPI): Promise<void> {
    const { prescriptionId } = historyService.getCurrentRouteParams();

    try {
      const pendingExams = deckPendingExams.getData() as ListPendingParams[];

      const requestBody: Record<string, Value> = {
        barcode,
        pendingExams
      }

      const params = {prescriptionNumber: Number(prescriptionId)} as Record<string, Value>;

      deckPendingExams.setLoading(true);
      this.programingClose = true;
      barcodeApi.close();

      const response = await HttpService.post(Endpoint.EXAM_COLLECT_READ_PENDING_BARCODE, params, { body: requestBody });

      barcodeApi.setOpen(true);
      deckPendingExams.setLoading(false);

      const responseBody = response.body as barcodeResponseType;

      const type = this.castAsReadPendingExamBarcodeResponseType(responseBody.responseType);

      switch(type){
        case ReadPendingExamBarcodeResponseType.SUBMIT_EXECUTED:
          this.handleSubmitExamCollectionListResponse(responseBody.confirmExamCollectionResponseList, fecthPendingExam);
          break;
        case ReadPendingExamBarcodeResponseType.ADD_EXAMS_TO_COLLECTION_LIST_SUCCESS:
          this.addBarcodeToCollectionList(responseBody.selectedExams, barcode, barcodeApi, deckPendingExams, fecthPendingExam);
          break;
        case ReadPendingExamBarcodeResponseType.CHANGE_EXAM_STATUS_SUCCESS:
          ToastHandler.showSuccessToast('tws.pending-exam-collection.exam-collection-success');
          ExamCollectionUtils.updateExamsOrRedirect(fecthPendingExam);
          break;
        case ReadPendingExamBarcodeResponseType.CHANGE_EXAM_STATUS_FAIL:
          ToastHandler.showErrorToast('tws.pending-exam-collection.change-exam-status-fail');
          break;
        case ReadPendingExamBarcodeResponseType.EXAM_NOT_IN_PRESCRIPTION_ERROR:
          ToastHandler.showBarcodeNotInPrescriptionErrorToast(prescriptionId as string);
          break;
        case ReadPendingExamBarcodeResponseType.INVALID_BARCODE_ERROR:
          ToastHandler.showErrorToast('tws.pending-exam-collection.invalid-barcode-error');
          break;
        case ReadPendingExamBarcodeResponseType.INVALID_COLLECTION_TIME_ERROR:
          ToastHandler.showInvalidCollectionTimeError(barcode);
          break;
        default:
          ToastHandler.showBarcodeNotInPrescriptionErrorToast(prescriptionId as string);
      }

    } catch {
      deckPendingExams.setLoading(false);
      ToastHandler.showBarcodeNotInPrescriptionErrorToast(prescriptionId as string);
    }
  }

  private static async submitSelectedExamList(selectedBarcodeExams: SelectedBarcodeExam[], deckPendingExams: DeckAPI, fecthPendingExam: FetchAPI): Promise<void> {
    try {
      if(selectedBarcodeExams.length === 0) {
        ToastHandler.showErrorToast('tws.pending-exam-collection.no-barcode-selected');
        return;
      }

      const { prescriptionId } = historyService.getCurrentRouteParams();
      const params = {prescriptionNumber: Number(prescriptionId)} as Record<string, Value>;

      const requestBody = selectedBarcodeExams as unknown as Record<string, Value>;

      deckPendingExams.setLoading(true);

      const response = await HttpService.post(Endpoint.EXAM_COLLECT_SUBMIT_EXAM_COLLECTION_LIST, params, { body: requestBody});

      deckPendingExams.setLoading(false);

      const responseBody = response.body as barcodeResponseType;

      this.handleSubmitExamCollectionListResponse(responseBody.confirmExamCollectionResponseList, fecthPendingExam);

    } catch(error) {
      const errorMessageList: string[] = this.beepedBarcodes.filter(beeped=>beeped.exam.nmExame).map(beeped=>beeped.exam.nmExame as string);

      ToastHandler.showExamCollectionFailErrorToast(errorMessageList, String(this.parameters.paramUseLaboratorySampleStandard));
    }
  }

  private static handleSubmitExamCollectionListResponse(confirmExamCollectionResponseList: ConfirmExamCollectionResponse[], fecthPendingExam: FetchAPI): void {
    ModalService.close(PendingExamsModalId);

    let hasSuccess = false;
    const errorMessageList: string[] = [];

    for(let i = 0; i < confirmExamCollectionResponseList.length; i++){

      const response = confirmExamCollectionResponseList[i];

      if(response.isSuccess) {
        hasSuccess = true;
      } else {
        errorMessageList.push(response.examDescription);
      }
    }

    if(hasSuccess) {
      ToastHandler.showSuccessToast('tws.pending-exam-collection.exam-collection-success');
      ExamCollectionUtils.updateExamsOrRedirect(fecthPendingExam);
    }

    if(errorMessageList.length > 0) {
      ToastHandler.showExamCollectionFailErrorToast(errorMessageList, String(this.parameters.paramUseLaboratorySampleStandard));
    }
  }

  private static addBarcodeToCollectionList(selectedExams: ListPendingParams[], barcode: string, barcodeApi: BarcodeAPI, deckPendingExams: DeckAPI, fecthPendingExam: FetchAPI): void {
    const { prescriptionId } = historyService.getCurrentRouteParams();
    if(selectedExams.length === 0){
      ToastHandler.showBarcodeNotInPrescriptionErrorToast(prescriptionId as string);
      return;
    }

    const deckData = deckPendingExams.getData() as ListPendingParams[];

    for(let i = 0; i < selectedExams.length; i++){

      const selectedExam = selectedExams[i];

      const alreadyBeeped = this.beepedBarcodes.some(beeped=>beeped.exam.nrSequencia === selectedExam.nrSequencia);
      if(alreadyBeeped) {
        continue;
      }

      const examIndex = deckData.findIndex(exam=>exam.nrSequencia === selectedExam.nrSequencia);
      if(examIndex < 0){
        continue;
      }

      this.beepedBarcodes.push({barcode, exam: selectedExam});
    }

    if(this.beepedBarcodes.length === deckData.length){
      this.openPendingExamsModal(barcodeApi, deckPendingExams, fecthPendingExam);
      return;
    }

    ToastHandler.showSuccessToast('tws.pending-exam-collection.success-barcode-read');
  }

  static onExitBarcode(evt: AbortableCloseEvent, barcodeApi: BarcodeAPI, deckPendingExams: DeckAPI, fecthPendingExam: FetchAPI): void {
    if (this.programingClose) {
      this.programingClose = false;
      return;
    }

    if(this.beepedBarcodes.length === 0 || this.closingBarcodeReader) {
      this.closingBarcodeReader = false;
      return;
    }
    evt.abort();
    this.notAllItemsAreReadModal(barcodeApi, deckPendingExams, fecthPendingExam);
  }

  private static selectedItemsWillBeLostAlertModal(barcodeApi: BarcodeAPI): void {
    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.pending-exam-collection.continue-reading',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => {
        this.closingBarcodeReader = true;
        barcodeApi.close();
        this.beepedBarcodes = [];
      },
      title: 'tws.pending-exam-collection.yes-return-action',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: 'tws.pending-exam-collection.items-will-be-lost-alert',
      dockButtons: buttons,
      focusOnOpen: true,
      title: 'text.message-alert-attention',
      type: ModalType.DIALOG,
      uuid: generateUUID(),
    }

    ModalService.openDialog(dialogModal);
  }

  private static notAllItemsAreReadModal(barcodeApi: BarcodeAPI, deckPendingExams: DeckAPI, fecthPendingExam: FetchAPI): void {
    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { this.selectedItemsWillBeLostAlertModal(barcodeApi); },
      title: 'tws.pending-exam-collection.return-to-list',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => {
        this.closingBarcodeReader = true;
        this.openPendingExamsModal(barcodeApi, deckPendingExams, fecthPendingExam);
      },
      title: 'tws.pending-exam-collection.finalize-reading',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: 'tws.pending-exam-collection.exist-not-selected-items',
      dockButtons: buttons,
      focusOnOpen: true,
      title: 'text.message-alert-attention',
      type: ModalType.DIALOG,
      uuid: generateUUID(),
    }

    ModalService.openDialog(dialogModal);
  }

  private static castAsReadPendingExamBarcodeResponseType(str: string): ReadPendingExamBarcodeResponseType {
    return ReadPendingExamBarcodeResponseType[str as keyof typeof ReadPendingExamBarcodeResponseType]
  }

  private static openPendingExamsModal(barcodeApi: BarcodeAPI, deckPendingExams: DeckAPI, fecthPendingExam: FetchAPI): void {
    this.closingBarcodeReader = true;
    barcodeApi.close();
    const wrappedSubmit = (selectedBarcodeExams: SelectedBarcodeExam[]): Promise<void> => {
      return this.submitSelectedExamList(selectedBarcodeExams, deckPendingExams, fecthPendingExam);
    }
    ModalHandler.openPendingExamsModal(this.beepedBarcodes, wrappedSubmit);
    this.beepedBarcodes = [];
  }
}

export {
  ExamCollectionWithBarcodeHandler
};
